if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.viewer.querySelectorAll('.slider-button').forEach((button) => {
          button.addEventListener('click', this.onDesktopViewerButtonClick.bind(this));
        });
        // Comforsyco: the visible nav arrows sit beside the thumbnail strip and
        // drive the MAIN image on every viewport. Capture phase so Dawn's default
        // "scroll the thumbnail list" handler on these buttons never runs.
        this.elements.thumbnails.addEventListener('click', this.onThumbnailArrowClick.bind(this), true);
        this.syncViewerCounter(this.elements.viewer.querySelector('[data-media-id].is-active'));
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        event.detail.currentElement.classList.add('is-active');
        this.syncViewerCounter(event.detail.currentElement);

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.syncViewerCounter(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      onThumbnailArrowClick(event) {
        const btn = event.target.closest('.slider-button');
        if (!btn || !this.elements.thumbnails.contains(btn)) return;
        if (!this.dataset.desktopLayout.includes('thumbnail')) return;

        // Take over: navigate the MAIN image instead of scrolling thumbnails.
        event.preventDefault();
        event.stopPropagation();

        // Visible thumbnails only — hidden variant duplicates must not be counted.
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumbnail) => thumbnail.offsetParent !== null
        );
        if (thumbnails.length < 2) return;

        const currentIndex = Math.max(
          thumbnails.findIndex((thumbnail) => thumbnail.querySelector('button[aria-current="true"]')),
          0
        );
        const increment = btn.name === 'next' ? 1 : -1;
        const nextIndex = Math.min(Math.max(currentIndex + increment, 0), thumbnails.length - 1);

        if (nextIndex === currentIndex) return;
        thumbnails[nextIndex].querySelector('button').click();
      }

      onDesktopViewerButtonClick(event) {
        if (!this.mql.matches || !this.dataset.desktopLayout.includes('thumbnail')) return;

        event.preventDefault();
        // Visible thumbnails only — products with variants render hidden
        // duplicate thumbnails (e.g. 39 total vs 18 visible). Counting the
        // hidden ones throws the index off and makes prev/next jump randomly.
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumbnail) => thumbnail.offsetParent !== null
        );
        if (thumbnails.length < 2) return;

        const currentIndex = Math.max(
          thumbnails.findIndex((thumbnail) => thumbnail.querySelector('button[aria-current="true"]')),
          0
        );
        const increment = event.currentTarget.name === 'next' ? 1 : -1;
        const nextIndex = Math.min(Math.max(currentIndex + increment, 0), thumbnails.length - 1);

        if (nextIndex === currentIndex) return;
        thumbnails[nextIndex].querySelector('button').click();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`);
        this.syncViewerCounter(activeMedia);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      syncViewerCounter(activeMedia) {
        const current = this.elements.viewer.querySelector('[data-media-counter-current]');
        const total = this.elements.viewer.querySelector('[data-media-counter-total]');
        if (!current || !total || !activeMedia) return;

        // Count actual slides, NOT every [data-media-id] element: each slide
        // carries the id on both its <li> and its zoom <button>, which doubled
        // the total (e.g. 16 images showed as "/32"). Index the active slide.
        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        const activeSlide = activeMedia.closest('.product__media-item') || activeMedia;
        const index = slides.indexOf(activeSlide);

        current.textContent = index >= 0 ? index + 1 : activeMedia.getAttribute('data-media-position') || 1;
        total.textContent = slides.length;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
